/**
 * Pedidos de Control de Kits — port de kit-stock/src/pages/Orders.tsx.
 * Transiciones + reversas de un paso, edición/borrado con aviso de ajuste de
 * stock, y factura PDF (ver/adjuntar/reemplazar). "solicitado" se muestra
 * "Pendiente de pago" (decisión del original).
 */

import { toastError } from '@/lib/toast-error';
import { useRef, useState } from 'react';
import { FileText, Paperclip, Loader2, Undo2, Pencil, Trash2 } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import { trpc } from '@/lib/trpc';
import { useKits } from './KitsModule';
import { uploadInvoice, validateInvoiceFile, openInvoice } from './invoice';
import type { KitOrderStatus } from '@/../../shared/types';
import { useTranslation } from 'react-i18next';
import { usePagination } from '@/hooks/usePagination';

type OrderRow = {
  orderId: string;
  officeId: string;
  officeName: string;
  quantity: number;
  note?: string;
  status: KitOrderStatus;
  createdAt: string;
  receivedAt?: string;
  hasInvoice: boolean;
  invoiceUploadedAt?: string;
  invoiceUploadedByName?: string;
};

const ORDER_STATUS_LABELS: Record<KitOrderStatus, string> = {
  borrador: 'Borrador',
  solicitado: 'Pendiente de pago',
  en_preparacion: 'En preparación',
  en_camino: 'En camino',
  recibido: 'Recibido',
  cancelado: 'Cancelado',
};

const ORDER_STATUS_COLORS: Record<KitOrderStatus, string> = {
  borrador: 'bg-gray-100 text-gray-700',
  solicitado: 'bg-blue-100 text-blue-700',
  en_preparacion: 'bg-yellow-100 text-yellow-700',
  en_camino: 'bg-orange-100 text-orange-700',
  recibido: 'bg-green-100 text-green-700',
  cancelado: 'bg-red-100 text-red-700',
};

function getAvailableTransitions(current: KitOrderStatus): KitOrderStatus[] {
  switch (current) {
    case 'borrador': return ['solicitado', 'cancelado'];
    case 'solicitado': return ['en_preparacion', 'cancelado'];
    case 'en_preparacion': return ['en_camino', 'cancelado'];
    case 'en_camino': return ['recibido', 'cancelado'];
    default: return [];
  }
}

// Reversa de un paso para deshacer un toque accidental. "recibido" no se
// revierte porque recibir ya sumó stock.
function getBackTransition(current: KitOrderStatus): KitOrderStatus | null {
  switch (current) {
    case 'solicitado': return 'borrador';
    case 'en_preparacion': return 'solicitado';
    case 'en_camino': return 'en_preparacion';
    default: return null;
  }
}

function InvoiceCell({ order }: { order: OrderRow }) {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const error = validateInvoiceFile(file);
    if (error) {
      toast.error(error);
      return;
    }
    setUploading(true);
    try {
      await uploadInvoice(utils.client, order.orderId, file);
      toast.success(t('kits.orders.factura_adjuntada'));
      utils.kits.listOrders.invalidate();
    } catch (err: any) {
      toastError(err, 'common.toast.no_se_pudo_adjuntar_la_factura');
    } finally {
      setUploading(false);
    }
  };

  const uploadedInfo =
    order.hasInvoice && order.invoiceUploadedAt
      ? `${order.invoiceUploadedByName ? `Subida por ${order.invoiceUploadedByName} el ` : 'Subida el '}${format(new Date(order.invoiceUploadedAt), 'dd/MM/yyyy', { locale: es })}`
      : null;

  return (
    <div className="space-y-0.5">
      <div className="flex items-center gap-1">
        <input ref={inputRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={onFileChange} />
        {order.hasInvoice && (
          <Button
            size="sm"
            variant="outline"
            title={uploadedInfo ?? undefined}
            onClick={() => openInvoice(utils.client, order.orderId).catch((e) => toastError(e, 'common.toast.no_se_pudo_abrir_la_factura'))}
          >
            <FileText className="h-4 w-4 mr-1" /> Ver
          </Button>
        )}
        <Button
          size="sm"
          variant="ghost"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          title={order.hasInvoice ? t('kits.orders.reemplazar_factura') : t('kits.orders.adjuntar_factura_pdf')}
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
          <span className="ml-1">{order.hasInvoice ? 'Reemplazar' : 'Adjuntar'}</span>
        </Button>
      </div>
      {uploadedInfo && (
        <p className="text-xs text-muted-foreground whitespace-nowrap">{uploadedInfo}</p>
      )}
    </div>
  );
}

function EditOrderDialog({ order, onClose }: { order: OrderRow; onClose: () => void }) {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  const [quantity, setQuantity] = useState(String(order.quantity));
  const [note, setNote] = useState(order.note ?? '');
  const updateMutation = trpc.kits.updateOrder.useMutation();

  const qty = parseInt(quantity, 10);
  const qtyValid = Number.isInteger(qty) && qty >= 1;
  const diff = qtyValid ? qty - order.quantity : 0;
  const isRecibido = order.status === 'recibido';

  const onSave = () => {
    if (!qtyValid) return;
    updateMutation.mutate(
      { orderId: order.orderId, quantity: qty, note },
      {
        onSuccess: () => {
          toast.success(`Pedido #${order.orderId} actualizado`);
          utils.kits.listOrders.invalidate();
          utils.kits.listOffices.invalidate();
          utils.kits.getOfficeSummary.invalidate({ officeId: order.officeId });
          onClose();
        },
        onError: (e) => toastError(e, 'common.toast.no_se_pudo_actualizar_el_pedido'),
      }
    );
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('kits.orders.editar_pedido')}{order.orderId}</DialogTitle>
          <DialogDescription>
            {isRecibido
              ? t('kits.orders.editar_recibido_desc')
              : t('kits.orders.editar_desc')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="order-qty">{t('kits.common.cantidad')}</Label>
            <Input
              id="order-qty"
              type="number"
              min={1}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
            {isRecibido && qtyValid && diff !== 0 && (
              <p className="text-sm text-amber-600">
                {diff > 0
                  ? t('kits.orders.se_sumaran_kits', { n: diff, office: order.officeName })
                  : t('kits.orders.se_restaran_kits', { n: -diff, office: order.officeName })}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="order-note">{t('kits.common.nota')}</Label>
            <Textarea id="order-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('common.ui.cancel')}</Button>
          <Button onClick={onSave} disabled={!qtyValid || updateMutation.isPending}>
            {updateMutation.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            {t('common.ui.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteOrderDialog({ order, onClose }: { order: OrderRow; onClose: () => void }) {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  const deleteMutation = trpc.kits.deleteOrder.useMutation();
  const isRecibido = order.status === 'recibido';

  const onConfirm = () => {
    deleteMutation.mutate(
      { orderId: order.orderId },
      {
        onSuccess: () => {
          toast.success(`Pedido #${order.orderId} eliminado`);
          utils.kits.listOrders.invalidate();
          utils.kits.listOffices.invalidate();
          utils.kits.getOfficeSummary.invalidate({ officeId: order.officeId });
          onClose();
        },
        onError: (e) => toastError(e, 'common.toast.no_se_pudo_eliminar_el_pedido'),
      }
    );
  };

  return (
    <AlertDialog open onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('kits.orders.eliminar_el_pedido')}{order.orderId}?</AlertDialogTitle>
          <AlertDialogDescription>
            {isRecibido
              ? t('kits.orders.eliminar_recibido_desc', { n: order.quantity, office: order.officeName })
              : t('kits.orders.eliminar_desc')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('common.ui.cancel')}</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={deleteMutation.isPending}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {deleteMutation.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            {t('common.ui.delete')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function KitsOrders() {
  const { t } = useTranslation();
  const { activeOfficeId, isGlobal } = useKits();
  const utils = trpc.useUtils();
  const [editingOrder, setEditingOrder] = useState<OrderRow | null>(null);
  const [deletingOrder, setDeletingOrder] = useState<OrderRow | null>(null);

  const { data: orders, isLoading } = trpc.kits.listOrders.useQuery({
    officeId: isGlobal ? undefined : activeOfficeId ?? undefined,
  });

  const { pageItems: pageOrders, footer: paginationFooter } = usePagination(orders ?? [], 25);

  const transitionMutation = trpc.kits.transitionOrder.useMutation();

  const handleTransition = (orderId: string, toStatus: KitOrderStatus, officeId: string) => {
    transitionMutation.mutate({ orderId, toStatus }, {
      onSuccess: () => {
        toast.success(`Pedido actualizado a ${ORDER_STATUS_LABELS[toStatus]}`);
        utils.kits.listOrders.invalidate();
        utils.kits.listOffices.invalidate();
        utils.kits.getOfficeSummary.invalidate({ officeId });
        utils.kits.listMovements.invalidate();
      },
      onError: (e) => toastError(e, 'common.toast.no_se_pudo_actualizar_el_pedido'),
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-foreground">{t('kits.common.pedidos')}</h1>
        <p className="text-muted-foreground mt-1">{t('kits.orders.gestiona_los_pedidos_de_kits_de')}</p>
      </div>

      <Card className="shadow-xs overflow-hidden">
        {isLoading ? (
          <div className="p-6 space-y-4">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : !orders || orders.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">
            <p className="text-lg">{t('kits.orders.no_hay_pedidos_registrados')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>{t('kits.common.clapbase')}</TableHead>
                  <TableHead>{t('kits.common.cantidad')}</TableHead>
                  <TableHead>{t('kits.common.estado')}</TableHead>
                  <TableHead>{t('kits.common.creado')}</TableHead>
                  <TableHead>{t('kits.common.recibido')}</TableHead>
                  <TableHead>{t('kits.common.nota')}</TableHead>
                  <TableHead>{t('kits.common.factura')}</TableHead>
                  <TableHead className="text-right">{t('kits.common.acciones')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageOrders.map((order) => {
                  const transitions = getAvailableTransitions(order.status);
                  const backStatus = getBackTransition(order.status);
                  return (
                    <TableRow key={order.orderId}>
                      <TableCell className="font-mono text-sm">#{order.orderId}</TableCell>
                      <TableCell className="font-medium">{order.officeName}</TableCell>
                      <TableCell className="font-bold">{order.quantity}</TableCell>
                      <TableCell>
                        <Badge className={ORDER_STATUS_COLORS[order.status]}>{ORDER_STATUS_LABELS[order.status]}</Badge>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{format(new Date(order.createdAt), 'dd MMM yyyy', { locale: es })}</TableCell>
                      <TableCell className="font-mono text-sm">{order.receivedAt ? format(new Date(order.receivedAt), 'dd MMM yyyy', { locale: es }) : '—'}</TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-xs truncate">{order.note || '—'}</TableCell>
                      <TableCell><InvoiceCell order={order} /></TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {transitions.map((toStatus) => (
                            <Button
                              key={toStatus}
                              size="sm"
                              variant="outline"
                              onClick={() => handleTransition(order.orderId, toStatus, order.officeId)}
                              disabled={transitionMutation.isPending}
                            >
                              {toStatus === 'cancelado'
                                ? t('common.ui.cancel')
                                : toStatus === 'recibido'
                                  ? t('kits.orders.marcar_recibido')
                                  : t('kits.orders.pasar_a', { status: ORDER_STATUS_LABELS[toStatus].toLowerCase() })}
                            </Button>
                          ))}
                          {backStatus !== null && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleTransition(order.orderId, backStatus, order.officeId)}
                              disabled={transitionMutation.isPending}
                              title={t('kits.orders.volver_a_status', { status: ORDER_STATUS_LABELS[backStatus].toLowerCase() })}
                            >
                              <Undo2 className="h-4 w-4 mr-1" />
                              {t('kits.orders.volver_a')} {ORDER_STATUS_LABELS[backStatus].toLowerCase()}
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditingOrder(order)}
                            title={t('kits.orders.editar_pedido_2')}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setDeletingOrder(order)}
                            title={t('kits.orders.eliminar_pedido')}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <div className="px-4 pb-3">{paginationFooter}</div>
          </div>
        )}
      </Card>

      {editingOrder && <EditOrderDialog order={editingOrder} onClose={() => setEditingOrder(null)} />}
      {deletingOrder && <DeleteOrderDialog order={deletingOrder} onClose={() => setDeletingOrder(null)} />}
    </div>
  );
}
